/**
 * Load full telemetry the same way as RideView: inline array, else telemetryDataUrl, else local snapshot.
 * Mutates a shallow copy of `data` and returns it.
 */

import { doc, updateDoc, deleteField } from 'firebase/firestore';
import { db } from '../firebase';
import { isValidTelemetryDataDownloadUrl } from './pendingRunsQueue';
import { loadRunTelemetrySnapshot } from './runTelemetrySnapshot';

/**
 * @param {string} rideId
 * @param {object} data — run document fields + id
 * @param {{ user: { uid: string } | null, navTelemetry?: any[] | null }} options
 * @returns {Promise<object>} same object reference, enriched
 */
export async function enrichRunWithTelemetryData(rideId, data, { user, navTelemetry } = {}) {
  if (!data || typeof data !== 'object') return data;

  if (navTelemetry && Array.isArray(navTelemetry) && navTelemetry.length > 0) {
    data.telemetryData = navTelemetry;
  }

  if (data.telemetryDataUrl != null && !isValidTelemetryDataDownloadUrl(data.telemetryDataUrl)) {
    delete data.telemetryDataUrl;
  }

  let stripTelemetryUrlFromServer = false;
  if (
    (!Array.isArray(data.telemetryData) || data.telemetryData.length === 0) &&
    isValidTelemetryDataDownloadUrl(data.telemetryDataUrl)
  ) {
    const url = data.telemetryDataUrl;
    try {
      const remoteTelemetryResp = await fetch(url);
      if (remoteTelemetryResp.ok) {
        const text = await remoteTelemetryResp.text();
        const trimmed = typeof text === 'string' ? text.trim() : '';
        if (trimmed && trimmed !== 'undefined' && trimmed !== 'null') {
          try {
            const parsed = JSON.parse(trimmed);
            if (Array.isArray(parsed)) {
              data.telemetryData = parsed;
            } else {
              stripTelemetryUrlFromServer = true;
            }
          } catch (parseErr) {
            console.warn('telemetryDataUrl response is not valid JSON:', parseErr?.message, String(trimmed || '').slice(0, 160));
            stripTelemetryUrlFromServer = true;
          }
        } else {
          stripTelemetryUrlFromServer = true;
        }
      } else {
        stripTelemetryUrlFromServer = true;
      }
    } catch (err) {
      console.warn('Failed to load telemetryDataUrl:', err);
      stripTelemetryUrlFromServer = true;
    }
  }

  if ((!Array.isArray(data.telemetryData) || data.telemetryData.length === 0) && user?.uid) {
    const snap = await loadRunTelemetrySnapshot(user.uid, rideId);
    if (Array.isArray(snap) && snap.length > 0) {
      data.telemetryData = snap;
    }
  }

  if (stripTelemetryUrlFromServer && user?.uid && typeof rideId === 'string' && !rideId.startsWith('local-') && !rideId.startsWith('queued-inline-')) {
    try {
      await updateDoc(doc(db, 'runs', rideId), { telemetryDataUrl: deleteField() });
    } catch (e) {
      console.warn('Could not remove corrupt telemetryDataUrl from run doc:', e);
    }
    delete data.telemetryDataUrl;
  }

  return data;
}
